import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { StoreBasicsForm } from '@/components/store-profile/StoreBasicsForm';
import { StoreLocationForm } from '@/components/store-profile/StoreLocationForm';
import { StoreCategoriesForm } from '@/components/store-profile/StoreCategoriesForm';
import { StoreSizesForm } from '@/components/store-profile/StoreSizesForm';
import { StoreHoursForm } from '@/components/store-profile/StoreHoursForm';
import { storeProfileSchema, type StoreProfileFormData } from '@/lib/validators';
import { merchantService } from '@/services/merchant.service';

export function StoreProfilePage() {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<StoreProfileFormData>({
    resolver: zodResolver(storeProfileSchema),
    defaultValues: {
      name: '',
      description: '',
      mall: '',
      floor: '',
      unit: '',
      latitude: 40.4093,
      longitude: 49.8671,
      categories: [],
      brands: [],
      availableSizes: {
        tops: [],
        bottoms: [],
        shoes: [],
      },
      operatingHours: [],
    },
  });

  const onSubmit = async (data: StoreProfileFormData) => {
    try {
      const stores = await merchantService.getMyStores();
      if (stores.length > 0) {
        await merchantService.updateStore(stores[0].id, data);
      }
      toast.success('Store profile saved successfully!');
    } catch (error) {
      toast.error('Failed to save store profile. Please try again.');
      console.error('Save error:', error);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Store Profile</h1>
        <p className="mt-2 text-muted-foreground">
          Set up your store details to start creating deals.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <StoreBasicsForm register={register} errors={errors} />

        <StoreLocationForm
          register={register}
          errors={errors}
          setValue={setValue}
          watch={watch}
        />

        <StoreCategoriesForm
          register={register}
          errors={errors}
          watch={watch}
          setValue={setValue}
        />

        <StoreSizesForm watch={watch} setValue={setValue} />

        <StoreHoursForm register={register} watch={watch} setValue={setValue} />

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Store Profile'}
          </Button>
        </div>
      </form>
    </div>
  );
}
